import { ImagePlusIcon } from "lucide-react";
import { useRef, useState } from "react";

import { useI18n } from "~/i18n";
import { compressImageForStash } from "~/lib/imageCompression";
import { cn } from "~/lib/utils";
import { MORROW_DICEBEAR_AVATAR_SEEDS, dicebearPersonAvatarUrl } from "./morrowPersonAvatarCatalog";
import { PERSON_AVATAR_RADIUS } from "./MorrowPersonCard";

/** ~300KB base64 budget — small enough for localStorage beside other profile fields. */
const PORTRAIT_DATA_URL_BUDGET_CHARS = 400_000;

export function MorrowPersonAvatarPicker({
  value,
  onChange,
  portraitImageUrl = "",
  onPortraitImageUrlChange,
  disabled = false,
}: {
  readonly value: string;
  readonly onChange: (avatarSeed: string) => void;
  readonly portraitImageUrl?: string;
  readonly onPortraitImageUrlChange?: (imageUrl: string) => void;
  readonly disabled?: boolean;
}) {
  const { t } = useI18n();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadPending, setUploadPending] = useState(false);
  const customPortrait = portraitImageUrl.trim();
  const canUpload = onPortraitImageUrlChange != null;

  const handleUpload = async (file: File) => {
    if (!onPortraitImageUrlChange) return;
    setUploadError(null);
    setUploadPending(true);
    try {
      const result = await compressImageForStash(file, PORTRAIT_DATA_URL_BUDGET_CHARS);
      if (!result.ok) {
        setUploadError(t("profile.avatar.uploadError"));
        return;
      }
      onPortraitImageUrlChange(result.image.dataUrl);
    } catch {
      setUploadError(t("profile.avatar.uploadError"));
    } finally {
      setUploadPending(false);
    }
  };

  return (
    <fieldset className="flex min-w-0 flex-col gap-2" disabled={disabled || uploadPending}>
      <legend className="text-xs font-medium uppercase tracking-[0.04em] text-muted-foreground">
        {t("profile.avatar.title")}
      </legend>
      <div className="rounded-lg border border-border/60 bg-muted/25 p-2">
        <div
          className={cn(
            "grid grid-cols-3 gap-2 overflow-y-auto overscroll-contain pr-0.5",
            // ~3 visible rows (9 cells); height tracks cell width in the modal.
            "max-h-[calc(3*((min(100%_18rem)-1rem)/3)+1rem)]",
          )}
        >
          {canUpload ? (
            <>
              <button
                type="button"
                aria-label={t("profile.avatar.upload")}
                aria-pressed={Boolean(customPortrait)}
                disabled={uploadPending}
                onClick={() => fileInputRef.current?.click()}
                className={cn(
                  "flex aspect-square flex-col items-center justify-center gap-1 overflow-hidden rounded-xl border-2 border-dashed bg-card p-1 text-muted-foreground transition-colors",
                  PERSON_AVATAR_RADIUS,
                  customPortrait
                    ? "border-primary ring-2 ring-inset ring-primary/30"
                    : "border-border hover:border-primary/40 hover:bg-accent/30 hover:text-foreground",
                )}
              >
                {customPortrait ? (
                  <img
                    alt=""
                    aria-hidden="true"
                    src={customPortrait}
                    className={cn("size-full object-cover", PERSON_AVATAR_RADIUS)}
                  />
                ) : (
                  <>
                    <ImagePlusIcon className="size-5 shrink-0" aria-hidden="true" />
                    <span className="px-1 text-center text-[10px] font-medium leading-tight">
                      {t("profile.avatar.upload")}
                    </span>
                  </>
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={(event) => {
                  const file = event.currentTarget.files?.[0];
                  event.currentTarget.value = "";
                  if (file) void handleUpload(file);
                }}
              />
            </>
          ) : null}
          {MORROW_DICEBEAR_AVATAR_SEEDS.map((seed) => {
            const selected = !customPortrait && value === seed;
            return (
              <button
                key={seed}
                type="button"
                aria-label={t("profile.avatar.choose", { character: seed })}
                aria-pressed={selected}
                onClick={() => {
                  onPortraitImageUrlChange?.("");
                  onChange(seed);
                }}
                className={cn(
                  "flex aspect-square items-center justify-center overflow-hidden rounded-xl border-2 bg-card p-1 transition-colors",
                  PERSON_AVATAR_RADIUS,
                  selected
                    ? "border-primary ring-2 ring-inset ring-primary/30"
                    : "border-border hover:border-primary/40 hover:bg-accent/30",
                )}
              >
                <img
                  alt=""
                  aria-hidden="true"
                  src={dicebearPersonAvatarUrl(seed)}
                  className={cn("size-full object-cover", PERSON_AVATAR_RADIUS)}
                />
              </button>
            );
          })}
        </div>
      </div>
      {uploadError ? <p className="text-xs text-destructive">{uploadError}</p> : null}
    </fieldset>
  );
}
